import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../firebase';
import './SavedStrokesPanel.css';

function SavedStrokesPanel({ 
    visible, 
    onClose, 
    onPlaceStroke, 
    strokeToSave = null // New prop for pre-selected stroke to save
}) {
    const [savedStrokes, setSavedStrokes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveModalData, setSaveModalData] = useState(null);
    const [saveName, setSaveName] = useState('');
    const [saveDescription, setSaveDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const canvasRef = useRef(null);

    // Calculate the center of the visible viewport for stroke placement
    const getViewportCenter = () => {
        // Get the viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate center position accounting for any UI panels
        // Assume some margin for toolbars/panels (adjust as needed)
        const centerX = viewportWidth / 2;
        const centerY = viewportHeight / 2;
        
        return { x: centerX, y: centerY };
    };

    useEffect(() => {
        if (visible) {
            loadSavedStrokes();
        }
    }, [visible]);

    // Auto-open save modal if strokeToSave is provided
    useEffect(() => {
        if (visible && strokeToSave) {
            openSaveModal(strokeToSave);
        }
    }, [visible, strokeToSave]);

    const loadSavedStrokes = async () => {
        setLoading(true);
        setError(null);

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                setError('Authentication required');
                return;
            }

            const response = await fetch(
                'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/saved-strokes/',
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'requesting-user-uid': user.uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setSavedStrokes(data.saved_strokes || []);
            } else {
                setError('Failed to load saved strokes');
            }
        } catch (error) {
            console.error('Error loading saved strokes:', error);
            setError('Failed to load saved strokes');
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceStroke = async (savedStroke) => {
        try {
            // Increment usage count on server
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (token) {
                await fetch(
                    `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/saved-strokes/${savedStroke.id}/use/`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'requesting-user-uid': user.uid,
                        },
                    }
                );

                // Update local usage count
                setSavedStrokes(prev => prev.map(stroke => 
                    stroke.id === savedStroke.id 
                        ? { ...stroke, usage_count: stroke.usage_count + 1 }
                        : stroke
                ));
            }
        } catch (error) {
            console.error('Error updating usage count:', error);
        }

        // Place the stroke at the center of the visible viewport
        const centerPosition = getViewportCenter();
        onPlaceStroke(savedStroke, centerPosition);
    };

    const handleDeleteStroke = async (strokeId) => {
        if (!window.confirm('Are you sure you want to delete this saved stroke?')) {
            return;
        }

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/saved-strokes/${strokeId}/`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'requesting-user-uid': user.uid,
                    },
                }
            );

            if (response.ok) {
                setSavedStrokes(prev => prev.filter(stroke => stroke.id !== strokeId));
            } else {
                setError('Failed to delete saved stroke');
            }
        } catch (error) {
            console.error('Error deleting saved stroke:', error);
            setError('Failed to delete saved stroke');
        }
    };

    const showSaveStrokeModal = (strokeData) => {
        setSaveModalData(strokeData);
        setSaveName('');
        setSaveDescription('');
        setShowSaveModal(true);
    };

    // Function to open save modal with a specific stroke
    const openSaveModal = (strokeData) => {
        if (strokeData) {
            showSaveStrokeModal(strokeData);
        }
    };

    const handleSaveStroke = async () => {
        if (!saveName.trim()) {
            setError('Please enter a name for the saved stroke');
            return;
        }

        if (savedStrokes.length >= 10) {
            setError('Maximum of 10 saved strokes allowed. Please delete an existing stroke first.');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                setError('Authentication required');
                return;
            }

            const response = await fetch(
                'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/saved-strokes/',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'requesting-user-uid': user.uid,
                    },
                    body: JSON.stringify({
                        name: saveName.trim(),
                        description: saveDescription.trim(),
                        stroke_data: saveModalData,
                        thumbnail_data: generateThumbnail(saveModalData)
                    })
                }
            );

            if (response.ok) {
                const newStroke = await response.json();
                setSavedStrokes(prev => [newStroke, ...prev]);
                setShowSaveModal(false);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to save stroke');
            }
        } catch (error) {
            console.error('Error saving stroke:', error);
            setError('Failed to save stroke');
        } finally {
            setSaving(false);
        }
    };

    const generateThumbnail = (strokeData) => {
        // Generate a simplified thumbnail representation
        if (!strokeData || !strokeData.points) return null;

        // Sample every nth point to create a simplified version
        const sampleRate = Math.max(1, Math.floor(strokeData.points.length / 20));
        const sampledPoints = strokeData.points.filter((_, index) => index % sampleRate === 0);

        return {
            points: sampledPoints,
            style: {
                color: strokeData.style?.color || '#000000',
                thickness: Math.max(1, (strokeData.style?.thickness || 2) / 2), // Thinner for thumbnail
                opacity: strokeData.style?.opacity || 1.0
            },
            tool: strokeData.tool || 'pen'
        };
    };

    const renderStrokeThumbnail = (savedStroke) => {
        const thumbnailData = savedStroke.thumbnail_data || savedStroke.stroke_data;
        if (!thumbnailData || !thumbnailData.points || thumbnailData.points.length === 0) {
            return <div className="stroke-thumbnail-placeholder">No preview</div>;
        }

        // Calculate bounding box
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        thumbnailData.points.forEach(point => {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
        });

        const width = Math.max(100, maxX - minX + 20); // Minimum width with padding
        const height = Math.max(60, maxY - minY + 20); // Minimum height with padding
        const scale = Math.min(80 / width, 50 / height); // Scale to fit in 80x50 px

        // Create SVG path
        const pathData = thumbnailData.points.map((point, index) => {
            const x = (point.x - minX + 10) * scale;
            const y = (point.y - minY + 10) * scale;
            return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
        }).join(' ');

        return (
            <svg 
                className="stroke-thumbnail" 
                width="80" 
                height="50" 
                viewBox="0 0 80 50"
            >
                <path
                    d={pathData}
                    stroke={thumbnailData.style?.color || '#000000'}
                    strokeWidth={Math.max(0.5, (thumbnailData.style?.thickness || 2) * scale)}
                    strokeOpacity={thumbnailData.style?.opacity || 1.0}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    };

    if (!visible) return null;

    return (
        <div className="saved-strokes-panel">
            <div className="panel-header">
                <h3>
                    <i className="fas fa-bookmark"></i>
                    Saved Strokes
                </h3>
                <button 
                    className="close-button" 
                    onClick={onClose}
                    title="Close"
                >
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <div className="panel-content">
                {error && (
                    <div className="error-message">
                        <i className="fas fa-exclamation-triangle"></i>
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="loading-state">
                        <i className="fas fa-spinner fa-spin"></i>
                        Loading saved strokes...
                    </div>
                ) : (
                    <>
                        <div className="strokes-grid">
                            {savedStrokes.map(savedStroke => (
                                <div key={savedStroke.id} className="saved-stroke-item">
                                    <div className="stroke-preview">
                                        {renderStrokeThumbnail(savedStroke)}
                                    </div>
                                    <div className="stroke-info">
                                        <h4 className="stroke-name" title={savedStroke.name}>
                                            {savedStroke.name}
                                        </h4>
                                        {savedStroke.description && (
                                            <p className="stroke-description" title={savedStroke.description}>
                                                {savedStroke.description}
                                            </p>
                                        )}
                                        <div className="stroke-stats">
                                            <span className="usage-count">
                                                Used {savedStroke.usage_count || 0} times
                                            </span>
                                        </div>
                                    </div>
                                    <div className="stroke-actions">
                                        <button
                                            className="place-button"
                                            onClick={() => handlePlaceStroke(savedStroke)}
                                            title="Place this stroke"
                                        >
                                            <i className="fas fa-plus"></i>
                                        </button>
                                        <button
                                            className="delete-button"
                                            onClick={() => handleDeleteStroke(savedStroke.id)}
                                            title="Delete this saved stroke"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {savedStrokes.length === 0 && (
                            <div className="empty-state">
                                <i className="fas fa-bookmark"></i>
                                <h4>No saved strokes yet</h4>
                                <p>
                                    To save a stroke:
                                </p>
                                <ol style={{ textAlign: 'left', padding: '0 20px', fontSize: '14px' }}>
                                    <li>Select the <strong>Select tool</strong> 📍</li>
                                    <li>Right-click on any stroke</li>
                                    <li>Choose "Save as Custom Stroke"</li>
                                </ol>
                                <p className="limit-info">
                                    You can save up to 10 custom strokes.
                                </p>
                            </div>
                        )}

                        {savedStrokes.length > 0 && (
                            <div className="panel-footer">
                                <span className="stroke-count">
                                    {savedStrokes.length} / 10 saved strokes
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Save Stroke Modal */}
            {showSaveModal && (
                <div className="SavedStrokesPanel__modal-overlay">
                    <div className="SavedStrokesPanel__save-stroke-modal">
                        <div className="SavedStrokesPanel__modal-header">
                            <h3>Save Custom Stroke</h3>
                            <button 
                                className="close-button" 
                                onClick={() => setShowSaveModal(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="SavedStrokesPanel__modal-content">
                            <div className="SavedStrokesPanel__best-practice-tip">
                                <i className="fas fa-lightbulb"></i>
                                <div>
                                    <strong>Tip:</strong> For best results, draw your stroke in one continuous motion without lifting your stylus or cursor click.
                                </div>
                            </div>
                            <div className="SavedStrokesPanel__form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={saveName}
                                    onChange={(e) => setSaveName(e.target.value)}
                                    placeholder="e.g., My Signature"
                                    maxLength={100}
                                />
                            </div>
                            <div className="SavedStrokesPanel__form-group">
                                <label>Description (optional)</label>
                                <textarea
                                    value={saveDescription}
                                    onChange={(e) => setSaveDescription(e.target.value)}
                                    placeholder="Optional description..."
                                    rows={3}
                                    maxLength={500}
                                />
                            </div>
                            <div className="stroke-count-info">
                                {savedStrokes.length} / 10 saved strokes used
                            </div>
                        </div>
                        <div className="SavedStrokesPanel__modal-footer">
                            <button 
                                className="SavedStrokesPanel__cancel-button"
                                onClick={() => setShowSaveModal(false)}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button 
                                className="SavedStrokesPanel__save-button"
                                onClick={handleSaveStroke}
                                disabled={saving || !saveName.trim()}
                            >
                                {saving ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        Save Stroke
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Expose the showSaveStrokeModal function for external use
SavedStrokesPanel.showSaveStrokeModal = function(strokeData) {
    // This will be called from the context menu
    const event = new CustomEvent('showSaveStrokeModal', { detail: strokeData });
    window.dispatchEvent(event);
};

export default SavedStrokesPanel;